import { useFilterParams } from "@/hooks/useFilterParams"
import { usePagination } from "@/hooks/usePagination"
import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../../../../../components/auth"
import { useGetSalonsLazyQuery } from "../../../../../graphql"
import Filter from "./components/Filter"
import List from "./components/List"
import "./index.less"

const ListBlock: FC = () => {
  const { region } = useAuth()
  const [getSalons] = useGetSalonsLazyQuery()

  const [loading, setLoading] = useState<boolean>(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false)

  const DEFAULT_FILTERS: { [key: string]: string | boolean | string[] } = useMemo(
    () => ({
      selectedStatus: [],
      selectedSM: "All",
      selectedName: "All",
      selectedEmail: "All",
      selectedInsiderStatus: "All",
      selectedRegion: region?.name ? region.name : "All",
      selectedDistributor: "All",
    }),
    [region.name]
  )

  const { page, pageSize, setPage, setPageSize } = usePagination()

  const { filters, setFilters, filtersKey } = useFilterParams(DEFAULT_FILTERS)

  const [searchParams] = useSearchParams()

  const [list, setList] = useState<any>()
  const [listForExport, setListForExport] = useState<any>()
  const [totalItems, setTotalItems] = useState<number>(0)

  const downloadBtnRef = useRef<HTMLButtonElement & { link: HTMLAnchorElement | null }>(null)

  const createQueryFilters = () => {
    const {
      selectedStatus,
      selectedEmail: email,
      selectedName: name,
      selectedRegion: region,
      selectedSM: salesManager,
      selectedDistributor: distributor,
      selectedInsiderStatus: insiderStatus,
    } = filters

    const statusConditions = []
    const selectedStatuses = selectedStatus as string[]

    if (selectedStatuses && selectedStatuses.length > 0 && !selectedStatuses.includes("All")) {
      if (selectedStatuses.includes("Blocked")) {
        statusConditions.push({ blocked: { eq: true } })
      }
      if (selectedStatuses.includes("Active")) {
        statusConditions.push({ isAppActivated: { eq: true }, blocked: { eq: false } })
      }
      if (selectedStatuses.includes("Inactive")) {
        statusConditions.push({
          or: [
            { isAppActivated: { eq: false }, blocked: { eq: false } },
            { isAppActivated: { eq: null }, blocked: { eq: false } },
          ],
        })
      }
    }

    return {
      ...(name !== "All" ? { name: { contains: name, notNull: true } } : {}),
      ...(region !== "All" ? { region: { name: { contains: region } } } : {}),
      ...(insiderStatus == "Allowed" ? { insiderPermission: { eq: true } } : {}),
      ...(insiderStatus == "Not Allowed"
        ? {
            or: [{ insiderPermission: { eq: false } }, { insiderPermission: { eq: null } }],
          }
        : {}),
      ...(distributor !== "All" ? { distributor: { name: { contains: distributor } } } : {}),
      ...(salesManager !== "All" ? { sales_manager: { name: { contains: salesManager } } } : {}),

      owner: {
        ...(email !== "All" ? { email: { contains: email, notNull: true } } : {}),
        ...(statusConditions.length > 0 ? { or: statusConditions } : {}),
      },
    }
  }

  const createList = async (isForExport = false) => {
    setLoading(true)
    if (list && !isForExport) setList(undefined)

    const filter = createQueryFilters() as UsersPermissionsUserFiltersInput

    const newList = await getSalons({
      fetchPolicy: "no-cache",
      errorPolicy: "all",
      variables: {
        pagination: {
          pageSize: isForExport ? 10000 : pageSize,
          page: isForExport ? undefined : page,
        },
        sort: "name:asc",
        filter,
      },
    })

    const totalItems = newList?.data?.salons?.meta?.pagination?.total || 0
    const finalData = newList?.data?.salons?.data

    if (finalData && !isForExport) {
      setTotalItems(totalItems!)
      setList(finalData)
      setLoading(false)
      return
    } else {
      setLoading(false)
      return finalData
    }
  }

  const getStatus = (item: any) => {
    const owner = item?.attributes?.owner?.data?.attributes
    return owner?.blocked ? "blocked" : owner?.isAppActivated ? "active" : "inactive"
  }

  const createExportList = async () => {
    setExportLoading(true)
    const list = await createList(true)

    let listForExport =
      list?.map((item: any) => [
        item?.attributes?.name || "-",
        item?.attributes?.owner?.data?.attributes?.email || "-",
        item?.attributes?.sales_manager?.data?.attributes?.name || "-",
        item?.attributes?.distributor?.data?.attributes?.name || "-",
        item?.attributes?.region?.data?.attributes?.name || "-",
        getStatus(item),
      ]) || []
    listForExport = listForExport?.map((item: any) => item.filter((entity: any) => entity !== null))
    listForExport.unshift(["NAME", "EMAIL", "SALES MANAGER", "DISTRIBUTOR", "REGION", "STATUS"])

    setExportLoading(false)
    setListForExport(listForExport)
  }

  const createSpecificFiltersList = () => {
    const list = []
    if (filters.selectedName !== "All")
      list.push({
        type: "name",
        color: "#D7A3AB",
        option: filters.selectedName,
        backgroundColor: "rgba(215, 163, 171, 0.15)",
      })
    if (filters.selectedEmail !== "All")
      list.push({
        type: "email",
        color: "#44C3BB",
        option: filters.selectedEmail,
        backgroundColor: "rgba(68, 195, 187, 0.15)",
      })
    if (filters.selectedSM !== "All")
      list.push({
        color: "#2bb350",
        type: "sales-manager",
        option: filters.selectedSM,
        backgroundColor: "rgba(11, 214, 42, 0.15)",
      })
    if (filters.selectedDistributor !== "All")
      list.push({
        color: "#3269a8",
        type: "distributor",
        option: filters.selectedDistributor,
        backgroundColor: "rgba(50, 105, 168, 0.15)",
      })

    return list
  }

  const specificFilters = createSpecificFiltersList()

  useEffect(() => {
    setListForExport(undefined)
    createList()
    // eslint-disable-next-line
  }, [pageSize, page, filtersKey])

  useEffect(() => {
    // eslint-disable-next-line
    listForExport && downloadBtnRef.current!.link!.click()
  }, [listForExport])

  useLayoutEffect(() => {
    const hasStatusInUrl = searchParams.has("selectedStatus")

    if (!hasStatusInUrl) {
      setFilters(
        {
          ...filters,
          selectedStatus: ["Active", "Inactive"],
        },
        false
      )
    }
    // eslint-disable-next-line
  }, [])

  return (
    <div className={"block-wrapper"}>
      <Filter
        {...{
          filters,
          setFilters,
          specificFilters,
          refresh: createList,
        }}
      />
      <List
        {...{
          list,
          page,
          loading,
          setPage,
          filters,
          pageSize,
          getStatus,
          setFilters,
          totalItems,
          setLoading,
          setPageSize,
          exportLoading,
          listForExport,
          downloadBtnRef,
          createExportList,
          createQueryFilters,
        }}
      />
    </div>
  )
}

export default ListBlock
